import type { UserRole } from "@/dashboard/features/userManagement/lib/schema";
import type { User } from "@/dashboard/types/user";

/**
 * Get the user's role for a specific tenant.
 * Super admins are treated as admins for all tenants.
 */
export function getRoleForTenant(user: User, tenantId: string): UserRole | null {
  if (user.superAdmin) {
    return "admin";
  }

  return user.tenantPermissions[tenantId]?.role ?? null;
}

/**
 * Check if user has at least the required role for a specific tenant.
 * Uses role hierarchy: member < manager < admin
 */
export function hasRoleForTenant(user: User, tenantId: string, requiredRole: UserRole): boolean {
  const userRole = getRoleForTenant(user, tenantId);

  if (!userRole) {
    return false;
  }

  const roleHierarchy: Record<UserRole, number> = {
    member: 1,
    manager: 2,
    admin: 3,
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Check if user has at least the required role for ANY tenant in their account.
 * Useful for account-level permissions.
 */
export function hasRoleForAnyTenant(user: User, requiredRole: UserRole): boolean {
  if (user.superAdmin) {
    return true;
  }

  const roleHierarchy: Record<UserRole, number> = {
    member: 1,
    manager: 2,
    admin: 3,
  };

  const requiredLevel = roleHierarchy[requiredRole];

  // Check if user has the required role in any tenant
  return Object.values(user.tenantPermissions).some(
    perm => roleHierarchy[perm.role] >= requiredLevel
  );
}
